import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarOff, ChevronLeft, ChevronRight, TimerOff, UserCheck, UserX } from 'lucide-react';
import { addDays, fmtTime, fmtWeekday, mondayOf } from '@siva/shared';
import { api, isUnreachable } from '../../lib/api.js';
import { LAST_HOURS, recall, remember } from '../../lib/offline.js';
import { OfflineNote } from '../../lib/OfflineNote.js';
import { errorMessage } from '../../lib/passkey.js';
import { fmtDate, Spinner, fmtMonth } from '../../lib/ui.js';
import { StatTile, Tabs } from '../../lib/components.js';
import { weekLabel } from '../../lib/week-label.js';
import { t } from '../../i18n/index.js';

type EarlyOutInfo = {
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  decision_note: string | null;
};
type DayRow = {
  date: string;
  status: 'present' | 'present_no_punchout' | 'absent' | 'weekly_off' | 'holiday';
  first_in: string | null;
  last_out: string | null;
  worked_minutes: number;
  target_minutes: number;
  shortfall_minutes: number;
  overtime_minutes: number;
  auto_closed: boolean;
  worked_on_off: boolean;
  live: boolean;
  early_out?: EarlyOutInfo;
  adjusted?: string | null;
};
type Totals = {
  worked: number;
  target: number;
  shortfall: number;
  overtime: number;
  present: number;
  no_punchout: number;
  absent: number;
  off: number;
  holiday: number;
};
type Hours = {
  month: string;
  today: string;
  week_start: string;
  week: { days: DayRow[]; totals: Totals };
  month_rows: { days: DayRow[]; totals: Totals };
  /** Published off days, future ones too, from a week before the month to a week after. */
  off_dates?: string[];
};
type Status = { open_shift: { since: string } | null; worked_minutes_running: number };
type View = 'week' | 'month';

const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;
const time = (iso: string | null) => (iso ? fmtTime(iso) : '—');
const monthOf = (ymd: string) => ymd.slice(0, 7);
const firstOf = (month: string) => `${month}-01`;
const monthsApart = (month: string, n: number) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const daysIn = (month: string) =>
  new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();
/** Monday-first weekday index, 0–6, from a YYYY-MM-DD. */
const mondayIndex = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
};

/** Days, not hours: the count of each kind of day in a range, closed days only. */
const dayCounts = (rows: DayRow[]) =>
  rows.reduce(
    (c, d) => {
      if (d.live) return c;
      if (d.status === 'present' || d.status === 'present_no_punchout') c.present++;
      if (d.status === 'present_no_punchout') c.no_punchout++;
      if (d.status === 'absent') c.absent++;
      if (d.status === 'weekly_off' || d.status === 'holiday') c.off++;
      return c;
    },
    { present: 0, no_punchout: 0, absent: 0, off: 0 },
  );

/**
 * The letter a day chip carries — P, A, WO, H, as the owner set them — and the colour class
 * that goes with it. A day with no punch-out is still a present day (it was auto-closed and
 * credited), so it is P in amber rather than a letter of its own; today carries no letter,
 * only its ring.
 */
const chipKind = (d: DayRow | undefined, future: boolean) => {
  if (!d) return { tag: '', cls: future ? 'future' : 'none' };
  if (d.live) return { tag: '', cls: 'live' };
  if (d.status === 'present_no_punchout') return { tag: t('hours_chip_present'), cls: 'nopo' };
  if (d.status === 'present') return { tag: t('hours_chip_present'), cls: 'present' };
  if (d.status === 'absent') return { tag: t('hours_chip_absent'), cls: 'absent' };
  if (d.status === 'weekly_off') return { tag: t('hours_chip_off'), cls: 'off' };
  return { tag: t('hours_chip_holiday'), cls: 'off' };
};

/** The word for what the day was, for the details under the calendar. */
const statusWord = (d: DayRow) => {
  if (d.live) return t('hours_chip_today_word');
  if (d.status === 'present_no_punchout') return t('hours_st_no_punchout');
  if (d.status === 'present')
    return d.worked_on_off ? t('hours_st_worked_on_off') : t('hours_st_present');
  if (d.status === 'absent') return t('hours_st_absent');
  if (d.status === 'weekly_off') return t('hours_st_off');
  return t('hours_st_holiday');
};

/**
 * A worker's days, one week or one month at a time, as the calendar of chips Weekly Off uses.
 *
 * Hours are a day's matter: a full-timer owes nine, a part-timer five, and each day is judged
 * against that alone. So the page shows days, not hours: a week is seven chips and a month is a
 * calendar of them, each saying what the day was — Present, Absent, Off, No Punch-Out — with
 * the counts above. A tapped day opens its own details underneath: in, out, hours against that
 * day's target, and what was said about an early out. No hours are added up across days; there
 * is no such figure here as "behind by 27 hours", and the owner took it off twice.
 */
export function HoursPage() {
  const [months, setMonths] = useState<Record<string, Hours>>({});
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [knownAt, setKnownAt] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState<View>('week');
  /** A date inside the range on screen. Null until the server has said what today is. */
  const [anchor, setAnchor] = useState<string | null>(null);
  /** The day whose details are open underneath the calendar. */
  const [picked, setPicked] = useState<string | null>(null);

  const current = useMemo(
    () => Object.values(months).find((m) => monthOf(m.today) === m.month) ?? null,
    [months],
  );
  const today = current?.today ?? null;

  const loadMonth = useCallback(async (month: string | null) => {
    const h = await api<Hours>(month ? `/me/hours?month=${month}` : '/me/hours');
    setMonths((all) => ({ ...all, [h.month]: h }));
    return h;
  }, []);

  // The current month and the live status, on load and every minute.
  useEffect(() => {
    const load = () =>
      Promise.all([loadMonth(null), api<Status>('/punch/status')])
        .then(([h, s]) => {
          setStatus(s);
          remember(LAST_HOURS, h);
          setKnownAt(null);
          setError(null);
          setAnchor((a) => a ?? h.today);
        })
        .catch((err) => {
          setError(errorMessage(err));
          // A month of days is worth reading without a signal; it is history, not live data.
          if (!isUnreachable(err)) return;
          const last = recall<Hours>(LAST_HOURS);
          if (!last) return;
          setMonths((all) =>
            all[last.value.month] ? all : { ...all, [last.value.month]: last.value },
          );
          setKnownAt(last.at);
          setAnchor((a) => a ?? last.value.today);
        });
    void load();
    const id = setInterval(() => void load(), 60_000);
    const onOnline = () => void load();
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(id);
      window.removeEventListener('online', onOnline);
    };
  }, [reloadKey, loadMonth]);

  // The range on screen.
  const range = useMemo(() => {
    if (!anchor) return null;
    if (view === 'week') {
      const start = mondayOf(anchor);
      return { start, end: addDays(start, 6) };
    }
    const m = monthOf(anchor);
    return { start: firstOf(m), end: `${m}-${String(daysIn(m)).padStart(2, '0')}` };
  }, [anchor, view]);

  // Any month the range touches that is not here yet is fetched; a week can straddle two.
  const needed = useMemo(
    () => (range ? [...new Set([monthOf(range.start), monthOf(range.end)])] : []),
    [range],
  );
  useEffect(() => {
    for (const m of needed) {
      if (months[m]) continue;
      if (today && m > monthOf(today)) continue; // the future has no days
      loadMonth(m).catch((err) => setError(errorMessage(err)));
    }
  }, [needed, months, today, loadMonth]);

  if (error && !current)
    return <OfflineNote at={null} onRetry={() => setReloadKey((n) => n + 1)} />;
  if (!current || !range || !today) return <Spinner />;

  const rows = needed
    .flatMap((m) => months[m]?.month_rows.days ?? [])
    .filter((d) => d.date >= range.start && d.date <= range.end)
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  const loading = needed.some((m) => !months[m] && m <= monthOf(today));
  const isCurrent =
    view === 'week' ? range.start === mondayOf(today) : monthOf(anchor!) === monthOf(today);
  // The dates are the heading; "This Week" / "This Month" is a note under them, only when it is
  // so. "Week of 31 Aug" over the same dates said it twice, and so did a month named twice.
  const label = view === 'week' ? weekLabel(range.start, range.end) : fmtMonth(monthOf(anchor!));
  const note = isCurrent ? (view === 'week' ? t('hours_this_week') : t('hours_this_month')) : '';
  const go = (a: string) => {
    setPicked(null);
    setAnchor(a);
  };
  const step = (dir: -1 | 1) =>
    go(
      view === 'week'
        ? addDays(mondayOf(anchor!), 7 * dir)
        : firstOf(monthsApart(monthOf(anchor!), dir)),
    );

  const counts = dayCounts(rows);
  // Weekly off as taken and still to come. The rows stop at today, so on their own the tile read
  // 0 until the off day had passed — "a bit confusing", the owner said. Taken is what the closed
  // days say (holidays included, as before) plus today when today is the off day; remaining is
  // the published off days still ahead in the range.
  const planned = new Set(
    [...needed.map((m) => months[m]), current].flatMap((h) => h?.off_dates ?? []),
  );
  const inRange = [...planned].filter((d) => d >= range.start && d <= range.end);
  const offTaken = counts.off + (inRange.includes(today) ? 1 : 0);
  const offLeft = inRange.filter((d) => d > today).length;
  // The summary card at the top (15 October): hours worked in the range against the target of the
  // days that have come so far. Today counts as it runs. Future days are left out — their targets
  // depend on off days and holidays not yet settled — so it says "so far" rather than guessing.
  const workedSum = rows.reduce(
    (t, d) => t + (d.live && status?.open_shift ? status.worked_minutes_running : d.worked_minutes),
    0,
  );
  const targetSum = rows.reduce((t, d) => t + d.target_minutes, 0);
  const pct = targetSum > 0 ? Math.min(100, Math.round((workedSum / targetSum) * 100)) : 0;
  const offHint =
    offTaken + offLeft === 0
      ? undefined
      : offLeft === 0
        ? t('hours_off_taken')
        : offTaken === 0
          ? t('hours_off_remaining')
          : t('hours_off_split', { taken: offTaken, remaining: offLeft });
  const pickedRow = picked ? rows.find((d) => d.date === picked) : undefined;
  const byDate = new Map(rows.map((d) => [d.date, d]));

  /** One day as a chip: weekday, number, the word for what the day was. */
  const chip = (date: string, withWeekday: boolean) => {
    const d = byDate.get(date);
    const future = date > today;
    const { tag, cls } = chipKind(d, future);
    return (
      <button
        key={date}
        type="button"
        role="gridcell"
        data-date={date}
        data-status={d?.status ?? (future ? 'future' : 'none')}
        className={`wo-chip day-chip ${cls} ${date === today ? 'today' : ''} ${picked === date ? 'selected' : ''}`}
        disabled={!d}
        aria-pressed={picked === date}
        onClick={() => setPicked((p) => (p === date ? null : date))}
      >
        {withWeekday && <span className="wd">{fmtWeekday(date).slice(0, 3)}</span>}
        <span className="dn">{Number(date.slice(8, 10))}</span>
        <span className="tag">{tag || ' '}</span>
      </button>
    );
  };

  const weekChips = (
    <div className="wo-strip hours-chips" role="grid" aria-label={label}>
      {Array.from({ length: 7 }, (_, i) => addDays(range.start, i)).map((d) => chip(d, true))}
    </div>
  );

  const monthChips = () => {
    const m = monthOf(anchor!);
    const offset = mondayIndex(firstOf(m));
    const cells: (string | null)[] = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= daysIn(m); d++) cells.push(`${m}-${String(d).padStart(2, '0')}`);
    return (
      <div className="wo-strip hours-chips month" role="grid" aria-label={fmtMonth(m)}>
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((w) => (
          <div key={w} className="dow" role="columnheader">
            {w}
          </div>
        ))}
        {cells.map((date, i) =>
          date ? (
            chip(date, false)
          ) : (
            <div key={`e${i}`} className="wo-chip empty" aria-hidden="true" />
          ),
        )}
      </div>
    );
  };

  /** The tapped day: what it was, and its own in, out and hours against its own target. */
  const detail = (d: DayRow) => {
    const running = d.live && status?.open_shift ? status.worked_minutes_running : null;
    const worked = running ?? d.worked_minutes;
    const diff = d.target_minutes > 0 ? worked - d.target_minutes : 0;
    // An off day, a holiday or an absence has nothing to say beyond what it was.
    const bare = d.status === 'weekly_off' || d.status === 'holiday' || d.status === 'absent';
    const tag = statusWord(d);
    return (
      <div className="picked-day" aria-live="polite">
        <h3>
          {fmtWeekday(d.date, 'long')}, {fmtDate(d.date, false)}
        </h3>
        <table className="kv">
          <tbody>
            <tr>
              <th scope="row">{t('hours_col_status')}</th>
              <td>
                {tag}
                {d.auto_closed && <span className="badge warn">{t('hours_no_punchout_mark')}</span>}
                {running !== null && <span className="badge good">{t('hours_running')}</span>}
                {d.early_out && (
                  <>
                    {' '}
                    <span
                      className={`badge ${d.early_out.status === 'approved' ? 'good' : d.early_out.status === 'rejected' ? 'bad' : 'warn'}`}
                    >
                      {t(
                        d.early_out.status === 'approved'
                          ? 'early_out_approved'
                          : d.early_out.status === 'rejected'
                            ? 'early_out_rejected'
                            : 'early_out_pending',
                      )}
                    </span>
                    {/* A phone has no hover, so the reason and the admin's answer are text. */}
                    <span className="early-out-said">
                      <span className="muted">
                        {t('hours_early_out_you_said', { reason: d.early_out.reason })}
                      </span>
                      {d.early_out.decision_note && (
                        <span className="muted">
                          {t('hours_early_out_admin_said', { note: d.early_out.decision_note })}
                        </span>
                      )}
                    </span>
                  </>
                )}
              </td>
            </tr>
            {(!bare || d.worked_on_off) && (
              <>
                <tr>
                  <th scope="row">{t('hours_col_in')}</th>
                  <td>{time(d.first_in)}</td>
                </tr>
                <tr>
                  <th scope="row">{t('hours_col_out')}</th>
                  <td>{running !== null ? '…' : time(d.last_out)}</td>
                </tr>
                <tr>
                  <th scope="row">{t('hours_col_hours')}</th>
                  <td>
                    {hm(worked)}
                    {d.target_minutes > 0 && (
                      <span className="muted"> / {hm(d.target_minutes)}</span>
                    )}
                  </td>
                </tr>
                {/* A day still open has no verdict, so no plus or minus against its target. */}
                {d.target_minutes > 0 && !running && !d.live && (
                  <tr>
                    <th scope="row">{t('hours_col_diff')}</th>
                    <td className={diff < 0 ? 'bad' : diff > 0 ? 'ok' : ''}>
                      {diff < 0 ? `−${hm(-diff)}` : diff > 0 ? `+${hm(diff)}` : '✓'}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="stack">
      {knownAt && <OfflineNote at={knownAt} onRetry={() => setReloadKey((n) => n + 1)} />}
      <section className="card hours-card">
        <div className="hours-head">
          <h2>{t('hours_title')}</h2>
          <Tabs<View>
            value={view}
            onChange={(v) => {
              setPicked(null);
              setView(v);
            }}
            items={[
              { value: 'week', label: t('hours_view_week') },
              { value: 'month', label: t('hours_view_month') },
            ]}
          />
        </div>
        <div className="range-nav" role="group" aria-label={t('hours_range')}>
          <button
            type="button"
            className="icon-btn small"
            aria-label={t('hours_earlier')}
            onClick={() => step(-1)}
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <span className="range-label">
            <strong>{label}</strong>
            {note && <small className="muted">{note}</small>}
          </span>
          <button
            type="button"
            className="icon-btn small"
            aria-label={t('hours_later')}
            disabled={isCurrent}
            onClick={() => step(1)}
          >
            <ChevronRight size={18} aria-hidden="true" />
          </button>
          {!isCurrent && (
            <button type="button" className="ghost small" onClick={() => go(today)}>
              {t('nav_today')}
            </button>
          )}
        </div>

        <div className="hours-hero">
          <span
            className="hours-ring"
            style={{ '--p': pct } as React.CSSProperties}
            role="img"
            aria-label={`${pct}%`}
          >
            <span>{pct}%</span>
          </span>
          <span className="hours-hero-text">
            <span className="hours-hero-label">
              {view === 'week' ? t('hours_worked_week') : t('hours_worked_month')}
            </span>
            <strong>{hm(workedSum)}</strong>
            <small>{t('hours_of_target', { target: hm(targetSum) })}</small>
          </span>
        </div>

        <div className="stat-row day-counts">
          <StatTile
            icon={UserCheck}
            tone="green"
            label={t('hours_days_present')}
            value={counts.present}
          />
          <StatTile icon={UserX} tone="red" label={t('hours_days_absent')} value={counts.absent} />
          <StatTile
            icon={CalendarOff}
            tone="blue"
            label={t('hours_days_off')}
            value={offTaken + offLeft}
            hint={offHint}
          />
          <StatTile
            icon={TimerOff}
            tone="amber"
            label={t('hours_days_no_out')}
            value={counts.no_punchout}
          />
        </div>

        {loading ? <Spinner /> : view === 'week' ? weekChips : monthChips()}
        {pickedRow
          ? detail(pickedRow)
          : !loading && <p className="muted small-text">{t('hours_tap_a_day')}</p>}
        <p className="muted">{t('hours_note')}</p>
      </section>
    </div>
  );
}
